import { FunctionComponent, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import useNonInitialEffect from '@versiondos/hooks';
import { Product, InventoryMovement } from '@spike/product-model';
import { fetchMovementsThunk, getProductThunk, ProductsStatus, saveMovementThunk } from '@spike/products-action';
import { showError, showSuccess } from '@spike/notifications-action';
import { RootState } from 'store';
import { useApiClientWrapper } from 'hooks';
import { SidePanel } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import clsx from 'clsx';
import AdjustStock, { StockAdjustmentMode } from './AdjustStock';
import StockHistory from './StockHistory';
import StockHistoryDetails from './StockHistoryDetails';
import { ProductHeader, formatMetric } from './ProductHeader';
import styles from './StockSidebar.module.scss';

interface StockSidebarProps {
    product: Product;
    onClose: () => void;
    onSaved: () => void;
}

type SidebarTab = 'adjust' | 'history';

const getCurrentStock = (product: Product): number => {
    const stock = product.businessProduct?.stock;
    const parsed = Number(stock);

    if (stock === undefined || stock === null || stock === '' || Number.isNaN(parsed)) {
        return 0;
    }

    return parsed;
};

export const StockSidebar: FunctionComponent<StockSidebarProps> = ({ product: initialProduct, onClose, onSaved }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const [tab, setTab] = useState<SidebarTab>('adjust');
    const [mode, setMode] = useState<StockAdjustmentMode>('add');
    const [quantity, setQuantity] = useState(1);
    const [unitCost, setUnitCost] = useState<number | undefined>(0);
    const [reason, setReason] = useState('');
    const [selectedMovement, setSelectedMovement] = useState<InventoryMovement | null>(null);

    const productFromStore = useSelector<RootState, Product | undefined>(state => state.products.product);
    const movements = useSelector<RootState, Array<InventoryMovement>>(state => state.products.movements.page ?? []);
    const status = useSelector<RootState, ProductsStatus>(state => state.products.status);
    const locationName = useSelector<RootState, string | undefined>(state => state.marketplace.marketplace?.name);

    const product = productFromStore && productFromStore.id === initialProduct.id ? productFromStore : initialProduct;
    const businessProductId = product.businessProduct?.id;
    const saving = status === ProductsStatus.SavingMovement;
    const loadingHistory = status === ProductsStatus.FetchingMovements;
    const canSave = quantity > 0 && reason.trim().length > 0 && !saving && businessProductId != null;

    useEffect(() => {
        if (tab !== 'history' || businessProductId == null) {
            return;
        }

        dispatch(
            fetchMovementsThunk(apiClientWrapper, businessProductId, undefined, [], '', undefined, undefined, 1, 100)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, businessProductId]);

    useNonInitialEffect(() => {
        if (status !== ProductsStatus.SaveMovementSuccess || businessProductId == null || product.id == null) {
            return;
        }

        setQuantity(1);
        setReason('');
        setUnitCost(0);
        setMode('add');
        dispatch(showSuccess('Stock adjustment created successfully.'));
        dispatch(getProductThunk(apiClientWrapper, product.id));
        dispatch(
            fetchMovementsThunk(apiClientWrapper, businessProductId, undefined, [], '', undefined, undefined, 1, 100)
        );
        onSaved();
    }, [status]);

    const handleSave = () => {
        if (!canSave || businessProductId == null) {
            return;
        }

        const delta = mode === 'add' ? quantity : -quantity;

        if (mode === 'reduce' && getCurrentStock(product) - quantity < 0) {
            dispatch(showError('Quantity cannot exceed available stock.'));
            return;
        }

        dispatch(saveMovementThunk(apiClientWrapper, businessProductId, delta, reason.trim(), undefined, unitCost));
    };

    const inventory = product.businessProduct?.inventory;
    const lowStockValue = inventory?.lowStockNotifications ? inventory.lowStockThreshold : undefined;
    const showingDetails = tab === 'history' && selectedMovement != null;

    const handleTabChange = (nextTab: SidebarTab) => {
        setSelectedMovement(null);
        setTab(nextTab);
    };

    return (
        <SidePanel
            side="right"
            width={600}
            showCloseButton
            onClose={onClose}
        >
            <div className={styles.panel}>
                <div className={styles.body}>
                    {showingDetails && selectedMovement ? (
                        <StockHistoryDetails
                            product={product}
                            movement={selectedMovement}
                            onBack={() => setSelectedMovement(null)}
                        />
                    ) : (
                        <>
                            <div className={styles.detailsHeader}>
                                <button
                                    type="button"
                                    className={styles.backButton}
                                    onClick={onClose}
                                    aria-label="Back to products"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </button>
                                <h2 className={styles.detailsTitle}>Stock</h2>
                            </div>
                            <ProductHeader
                                product={product}
                                trackingLabel="Track Total Stock"
                            />
                            <div className={styles.metrics}>
                                <div className={styles.metricCard}>
                                    <span className={styles.metricLabel}>Quantity</span>
                                    <span className={styles.metricValue}>
                                        {formatMetric(product.businessProduct?.stock)}
                                    </span>
                                </div>
                                <div className={styles.metricCard}>
                                    <span className={styles.metricLabel}>Low Stock</span>
                                    <span className={styles.metricValue}>{formatMetric(lowStockValue)}</span>
                                </div>
                            </div>
                            <div className={styles.tabs}>
                                <button
                                    type="button"
                                    className={clsx(styles.tab, tab === 'adjust' && styles.tabActive)}
                                    onClick={() => handleTabChange('adjust')}
                                >
                                    Adjust Stock
                                </button>
                                <button
                                    type="button"
                                    className={clsx(styles.tab, tab === 'history' && styles.tabActive)}
                                    onClick={() => handleTabChange('history')}
                                >
                                    Stock History
                                </button>
                            </div>
                            {tab === 'adjust' ? (
                                <AdjustStock
                                    mode={mode}
                                    quantity={quantity}
                                    unitCost={unitCost}
                                    reason={reason}
                                    currentStock={getCurrentStock(product)}
                                    disabled={saving}
                                    onModeChange={setMode}
                                    onQuantityChange={setQuantity}
                                    onUnitCostChange={setUnitCost}
                                    onReasonChange={setReason}
                                />
                            ) : (
                                <StockHistory
                                    movements={movements}
                                    loading={loadingHistory}
                                    locationName={locationName}
                                    onViewDetails={setSelectedMovement}
                                />
                            )}
                        </>
                    )}
                </div>
                {tab === 'adjust' && !showingDetails && (
                    <div className={styles.footer}>
                        <Button
                            label="Save"
                            size="large"
                            variant="primary"
                            className={styles.saveButton}
                            fullWidth
                            onClick={handleSave}
                            disabled={!canSave}
                            isLoading={saving}
                        />
                    </div>
                )}
            </div>
        </SidePanel>
    );
};

export default StockSidebar;
